import { AxiosResponse } from 'axios';

import { store } from '@/data/libraries/Store';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { updateCartRedux, removeCartRedux } from '@/ui/store/slices/cartSlice';
import { RootState } from '@/ui/store/store';

import CartLocalStorage from '@/data/localStorage/CartLocalStorage';

import OrdersRepository from '@/data/repositories/OrdersRepository';
import { updateCart } from '@/domain/useCases/updateCartUseCase';
import ProductsController from '@/controllers/ProductsController';

import { translate } from '@/domain/utils/translate';

import { ICart, ICartDetail, ICartResponse, IClientDetail } from '@/domain/interfaces/ICart';
import { IWebPayResponse } from '@/domain/interfaces/IPayment';
import { IProductResponse } from '@/domain/interfaces/IProductResponse';
import { IBackErrorDTO } from '@/domain/interfaces/IBackErrors';

/** Representa el controlador que maneja los eventos de los casos de uso del carrito
 * @returns {object} Funciones del controlador
 */
const CartController = () => {
  /** Actualización de Redux */
  const dispatch = useAppDispatch();

  /**Trae la información del carrito desde Redux */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  /**
   * Instancia del Repository
   * @const {OrdersRepository}
   */
  const ordersRepository = OrdersRepository.instance;

  /**
   * Instancia del LocalStorage
   * @const {CartLocalStorage}
   */
  const cartLocalStorage = CartLocalStorage.instance;
  /** Funciones del caso de uso actualizar carrito */
  const { addArticle, deleteArticle, isInCart, getArticleFromCart, getCart, validateCartUseCase, calculateAmounts } = updateCart();

  const { getProductBySku } = ProductsController();

  const CART_UPDATES_WARNING_MESSAGE: string = translate('cart.cart_updates_warning_message');

  const updateCartProductsData = async (): Promise<ICartResponse> => {
    /** Objeto de respuesta inicial */
    let response: ICartResponse = {
      error: false,
      message: '',
      cart: null,
    };

    const cart = getCart();
    if (!cart) {
      return response;
    }

    const articlesInCart = cart.details ?? [];

    for (const article of articlesInCart) {
      try {
        const result = await addToCart(article);
        response = result;
      } catch (error) {
        /** Error eliminado */
      }
    }

    return response;
  };

  /** Agrega un articulo al carrito */
  const addToCart = async (article: ICartDetail): Promise<ICartResponse> => {
    /** Objeto de respuesta */
    let response: ICartResponse = {
      error: false,
      message: '',
      cart: null,
    };

    await getProductBySku(article.skuProduct, store.DEFAULT)
      .then((productResponse: IProductResponse) => {
        if (productResponse.products.length > 0) {
          article.price = productResponse.products[0].price;
          article.offerPrice = productResponse.products[0].offerPrice;
          article.title = productResponse.products[0].title;
          productResponse.products[0].multimedia && (article.image = productResponse.products[0].multimedia[0]);

          article.status = productResponse.products[0].status;
          article.quantityAvailable = article.status !== 1 ? 0 : productResponse.products[0].quantityAvailable;

          article.attributes.splice(0, article.attributes.length, ...(productResponse.products[0].attributes ?? []));
          article.taxes.splice(0, article.taxes.length, ...(productResponse.products[0].taxes ?? []));

          article.shippingTypes.splice(0, article.shippingTypes.length, ...(productResponse.products[0].shippingTypes ?? []));
        }
      })
      .finally(() => {
        response = addArticle(article);
        if (!response.error) {
          if (response.cart !== undefined && response.cart !== null) {
            const newCart = JSON.stringify(omitPropertiesOnCompare(response.cart.details));
            const currentCart = JSON.stringify(omitPropertiesOnCompare(cartProducts.details));

            if (newCart !== currentCart) {
              response.error = true;
              response.message = CART_UPDATES_WARNING_MESSAGE;
            }
            /** Actualiza el carrito en Redux */
            dispatch(updateCartRedux(JSON.parse(JSON.stringify(response.cart))));
          } else {
            /** Actualiza el carrito en Redux */
            dispatch(removeCartRedux());
          }
        }
        return response;
      });
    return response;
  };

  const validateCart = (): ICartResponse => {
    return validateCartUseCase();
  };

  /** Actualiza la información del cliente en el carrito */
  const updateClientInCart = (client: IClientDetail) => {
    const cart = getCart();
    if (cart === null) {
      return 0;
    } else {
      cart.clientDetails = client;
      cartLocalStorage.updateCart(cart);
      dispatch(updateCartRedux(JSON.parse(JSON.stringify(cart))));
    }
  };

  /** Actualiza la información del cliente en el carrito */
  const updateCartMainInfo = (mainCartData: ICart) => {
    const cart = getCart();

    if (cart && mainCartData) {
      cart.gatewayId = mainCartData.gatewayId;
      cart.reservationId = mainCartData.reservationId;
      cart.clientId = mainCartData.clientId;

      cartLocalStorage.updateCart(cart);
      dispatch(updateCartRedux(JSON.parse(JSON.stringify(cart))));
    }
  };

  /** Actualiza la información de los montos en el carrito */
  const updateShippingTypeCost = (amount: number) => {
    const cart = getCart();
    if (cart === null) {
      return 0;
    } else {
      cart.totalShipping = amount;
      cartLocalStorage.updateCart(cart);
      calculateAmounts();
      dispatch(updateCartRedux(JSON.parse(JSON.stringify(cart))));
    }
  };

  /** Elimina un articulo del carrito */
  const deleteFromCart = (article: ICartDetail): ICartResponse => {
    const response: ICartResponse = deleteArticle(article);

    if (!response.error) {
      if (response.cart === null) {
        /** Actualiza el carrito en Redux */
        dispatch(removeCartRedux());
      } else {
        dispatch(updateCartRedux(JSON.parse(JSON.stringify(response.cart))));
      }
    }

    return response;
  };

  /** Consulta si el producto esta en el carrito por su SKU */
  const articleInCart = (sku: string): boolean => {
    return isInCart(sku);
  };

  /** Devuelve la cantidad de artículos agregados en el carrito */
  const productQuantity = (): number => {
    const article = getCart();

    if (article === null) {
      return 0;
    } else {
      return article.totalProducts ?? 0;
    }
  };

  /** Devuelve la cantidad de artículos agregados en el carrito por su SKU */
  const productQuantityBySku = (sku: string): number => {
    const article = getArticleFromCart(sku) as ICartDetail | null;

    if (article === null) {
      return 0;
    } else {
      return article.quantity ?? 0;
    }
  };

  /** Genera la orden de compra y da como respuesta el Token y la URL */
  const purchaseOrder = async (cart: ICart): Promise<IWebPayResponse | number> => {
    /** Errores asociados a la reserva */
    const RESERVATION_ERRORS_CODES = ['ORD029', 'ORD028', 'ORD030'];

    const result = await ordersRepository
      .purchaseOrder(cart)
      .then((result: AxiosResponse<IWebPayResponse>) => {
        return result.data;
      })
      .catch((error) => {
        if (error.response) {
          const errors: IBackErrorDTO[] = error.response.data;
          const findReservationError = errors.find((error) => RESERVATION_ERRORS_CODES.includes(error.code));

          if (findReservationError) {
            return 500;
          } else {
            return error.response.status;
          }
        } else {
          return 500;
        }
      });

    return result;
  };

  /** Elimina el carrito */
  const deleteCart = (): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        /** Primero eliminar el carrito del localStorage */
        cartLocalStorage.deleteCart();

        /** Luego actualizar Redux inmediatamente */
        dispatch(removeCartRedux());

        /** Devolver éxito después de un pequeño retraso para asegurar que las operaciones se completen */
        setTimeout(() => {
          resolve(true);
        }, 100);
      } catch (error) {
        /** Error eliminado */
        /** Intentar eliminar el carrito de todas formas */
        cartLocalStorage.deleteCart();
        dispatch(removeCartRedux());

        setTimeout(() => {
          resolve(false);
        }, 100);
      }
    });
  };

  const omitPropertiesOnCompare = (cartDetails) => {
    if (!cartDetails) {
      return;
    }
    return cartDetails.map((item) => {
      const { quantityAvailable, ...rest } = item; /** Desestructuramos y omitimos quantityAvailable */
      return rest; /** Retornamos el objeto sin quantityAvailable */
    });
  };

  /** retorna la funciones del controlador */
  return {
    addToCart,
    deleteFromCart,
    articleInCart,
    productQuantityBySku,
    productQuantity,
    purchaseOrder,
    deleteCart,
    updateCartProductsData,
    updateClientInCart,
    updateShippingTypeCost,
    validateCart,
    updateCartMainInfo,
  };
};

export default CartController;
